/**
 * 布局采集（T04）。
 *
 * 目标：把真实 DOM 的物理像素矩形换算成 **390 x 844 逻辑坐标**（dp），
 * 供 `docs/UI-COMPONENT-MANIFEST.md` / `public/component-manifest.json` 使用。
 *
 * PhoneFrame 在窗口小于画布时会用 CSS `transform: scale(s)` 整体缩放，
 * 因此物理像素与逻辑像素之间相差一个 scale：
 *
 *   logicalX = (physicalLeft - frameLeft) / scale
 *
 * 采集分两条路径，同一套换算逻辑：
 * 1. 浏览器 / 真实布局：读 `getBoundingClientRect()`（有非零宽高时走这条）。
 * 2. jsdom / 静态采集：jsdom 不做布局，`getBoundingClientRect()` 全为 0，
 *    此时回退读取元素内联声明盒（`left/top/width/height`）。
 *    `npm run manifest` 走的就是第 2 条，因此生成过程不依赖浏览器。
 */

/** 逻辑画布宽（dp），与 PhoneFrame 保持一致。 */
export const FRAME_WIDTH = 390;
/** 逻辑画布高（dp），与 PhoneFrame 保持一致。 */
export const FRAME_HEIGHT = 844;

/** 逻辑坐标下的外接矩形，即 ComponentMeta.anchor。 */
export interface Anchor {
  frameWidth: 390;
  frameHeight: 844;
  x: number;
  y: number;
  width: number;
  height: number;
}

/** 只取换算需要的矩形字段，方便从 DOMRect 或声明盒构造。 */
export interface RectLike {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** 画布左上角在视口中的物理位置。 */
export interface Point {
  x: number;
  y: number;
}

export interface MeasureOptions {
  /** PhoneFrame 的缩放系数，默认 1。 */
  scale?: number;
  /** 画布左上角物理坐标，默认 {0,0}。 */
  origin?: Point;
}

/** 保留 2 位小数，避免浮点噪声写进清单。 */
function round(value: number, digits = 2): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

function normalizeScale(scale: number | undefined): number {
  return typeof scale === 'number' && Number.isFinite(scale) && scale > 0 ? scale : 1;
}

/** 把物理像素矩形换算成 390x844 逻辑坐标矩形。 */
export function toLogicalAnchor(rect: RectLike, options: MeasureOptions = {}): Anchor {
  const scale = normalizeScale(options.scale);
  const origin = options.origin ?? { x: 0, y: 0 };
  return {
    frameWidth: FRAME_WIDTH,
    frameHeight: FRAME_HEIGHT,
    x: round((rect.left - origin.x) / scale),
    y: round((rect.top - origin.y) / scale),
    width: round(rect.width / scale),
    height: round(rect.height / scale),
  };
}

/** 读取画布缩放：优先用真实布局宽度，jsdom 下回退解析内联 `transform: scale()`。 */
export function readCanvasScale(frame: HTMLElement | null): number {
  if (frame === null) return 1;

  const rect = frame.getBoundingClientRect();
  if (rect.width > 0) {
    return normalizeScale(round(rect.width / FRAME_WIDTH, 4));
  }

  const transform = frame.style.transform;
  const matched = /scale\(\s*([0-9]*\.?[0-9]+)\s*\)/.exec(transform);
  const parsed = matched?.[1] === undefined ? Number.NaN : Number(matched[1]);
  return normalizeScale(parsed);
}

/** 读取画布左上角的物理坐标；jsdom 下退化为 {0,0}。 */
export function readCanvasOrigin(frame: HTMLElement | null): Point {
  if (frame === null) return { x: 0, y: 0 };
  const rect = frame.getBoundingClientRect();
  if (rect.width <= 0 && rect.height <= 0) return { x: 0, y: 0 };
  return { x: rect.left, y: rect.top };
}

function parsePx(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const matched = /^(-?[0-9]*\.?[0-9]+)px$/.exec(trimmed);
  const raw = matched?.[1];
  if (raw === undefined) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * 读取元素内联声明的盒子（jsdom 静态采集路径）。
 * 四个值缺一不可，否则视为无法测量。
 */
export function readDeclaredBox(element: HTMLElement): RectLike | null {
  const style = element.style;
  if (style === undefined) return null;
  const left = parsePx(style.left);
  const top = parsePx(style.top);
  const width = parsePx(style.width);
  const height = parsePx(style.height);
  if (left === null || top === null || width === null || height === null) return null;
  return { left, top, width, height };
}

/**
 * 采集单个元素的逻辑坐标。
 * 有真实布局就用 `getBoundingClientRect()`，否则回退内联声明盒；两者都拿不到返回 null。
 */
export function measureElement(
  element: HTMLElement | null,
  options: MeasureOptions = {},
): Anchor | null {
  if (element === null) return null;

  const rect = element.getBoundingClientRect();
  const hasRealLayout = rect.width > 0 || rect.height > 0;

  // jsdom / 静态采集：`getBoundingClientRect()` 全为 0，回退读元素内联声明盒。
  // 声明盒（left/top/width/height）本身就是 390x844 逻辑坐标（dp），
  // 不能再套一次 scale，否则「有 scale、无布局」时锚点会被越除越大。
  if (!hasRealLayout) {
    const declared = readDeclaredBox(element);
    return declared === null ? null : toLogicalAnchor(declared);
  }

  // 真实布局：物理像素 → 逻辑坐标（减 origin、除 scale），换算语义保持不变。
  return toLogicalAnchor(
    { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
    options,
  );
}

/** 便捷入口：自动读取画布的 scale 与原点，再采集元素。 */
export function measureInFrame(
  element: HTMLElement | null,
  frame: HTMLElement | null,
): Anchor | null {
  return measureElement(element, {
    scale: readCanvasScale(frame),
    origin: readCanvasOrigin(frame),
  });
}
